// This module is the single translator from RTK Query failures into Sentry
// events. The `[Infra]` / `[Auth]` / `[Resource]` message prefixes, the
// fingerprints, and the ignore rules below are all defined here. Triaging a
// Sentry issue and wondering what a prefix or tag means? See the full taxonomy:
// `.claude/docs/web-sentry-error-tracking.md`.
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/nextjs";

import { redact } from "@/lib/logError";
import type { ExceptionDetail, OtopRequestState } from "@generated/types";

import {
  GATEWAY_STATUSES,
  readTransportFailure,
  type TransportFailure,
} from "./shouldRetryRequest";

/**
 * Type guard to check if the error data is an ExceptionDetail from our BFF
 */
function isExceptionDetail(data: unknown): data is ExceptionDetail {
  return (
    typeof data === "object" &&
    data !== null &&
    "status" in data &&
    typeof (data as ExceptionDetail).status === "number" &&
    "origin" in data &&
    typeof (data as ExceptionDetail).origin === "string" &&
    "type" in data &&
    typeof (data as ExceptionDetail).type === "string"
  );
}

/**
 * HTTP status codes that are expected during normal app operation
 * and should NOT be reported to Sentry.
 *
 * - 401: Token expiry → auth provider refreshes transparently, not actionable
 * - 422: Validation error → Form validation handles these client-side
 *
 * NOT skipped: 409. A 409 Conflict means the client attempted something the
 * server's state forbids (stale cache, double-submit, illegal state
 * transition) — that is almost always a real client-side bug, not routine
 * "business logic the UI handles". It is captured as `warning` severity (see
 * getSeverityFromStatus) so it's visible for triage without reading as fatal.
 * If a SPECIFIC routine 409 later proves noisy, mute that fingerprint at the
 * Sentry project level rather than re-adding a blanket app-side skip.
 * (Removed 2026-07: a genuine onboarding submit-state desync — the app
 * blindly re-submitting an already-submitted application — was invisible under
 * the old blanket 409 skip.)
 *
 * Tracked codes (reported as "warning"):
 * - 403: Permission denied → tagged `auth.forbidden` (may indicate role misconfig)
 * - 404: Not found → tagged `resource.notFound` (may indicate stale references)
 */
const IGNORED_HTTP_STATUS_CODES = new Set([401, 422]);

/**
 * Exception types from upstream that ALWAYS represent bugs, never user input —
 * even when marshalled as a 4xx. `internal-service-exception` is the upstream
 * "we hit an unexpected error" type (e.g. Java NPE). `constraint-violation-exception`
 * is Bean Validation's NPE/illegal-state surface, sometimes returned as 400.
 * Both must remain visible in Sentry.
 *
 * NOTE: This list is intentionally duplicated in
 * `apps/bff/src/common/interceptors/errors.interceptor.ts` (as `ALWAYS_CAPTURE_TYPES`).
 * Cross-app sharing isn't trivial in this monorepo, so the two stay in sync
 * by convention. If you change one, change both.
 */
const ALWAYS_BUG_EXCEPTION_TYPES = new Set([
  "internal-service-exception",
  "constraint-violation-exception",
]);

/**
 * RTK Query endpoints where a **409 is a routine, user-driven business
 * conflict** that the UI already explains — not the stale-cache /
 * double-submit / illegal-transition client bug a 409 usually signals.
 *
 * Deliberately keyed on the RTK Query **endpoint name**, not on a blanket
 * status skip and not on the upstream `messages[]` copy (that copy is
 * `@endor-group` wording that can change without notice). The blanket 409 skip
 * was removed in 2026-07 because it hid a genuine onboarding submit-state
 * desync — scoping the exemption per-endpoint keeps every other 409 visible,
 * including that one.
 *
 * Before adding an endpoint here, confirm BOTH: the conflict is caused by user
 * input the client cannot pre-validate, AND the UI already tells the user how
 * to recover. If the copy is generic ("please try again"), the honest fix is
 * better copy, not an exemption.
 */
const ROUTINE_CONFLICT_ENDPOINTS = new Set([
  // "A registration with this phone number already exists" — a visitor typed a
  // number that is already registered. The client cannot know that in advance,
  // and `errors.sendSmsOtpFailed` already says "…hasn't already been
  // registered". CUSTOMER-UI-6Z.
  "sendRegistrationSmsOtp",
  // "A registration with this email already exists" — the email twin of the
  // above, and identical on both gates: the client cannot pre-check an address
  // (that would be an account-enumeration oracle) and `errors.sendEmailOtpFailed`
  // already says "…hasn't already been registered". Missed when 6Z was fixed;
  // kept firing on the same release. CUSTOMER-UI-6X.
  //
  // Covers resend too — the resend button re-calls this same endpoint, there is
  // no separate resendRegistrationEmailOtp.
  "sendRegistrationEmailOtp",
]);

/**
 * Upstream microservice exception types that represent user-input failures
 * (wrong OTP, indicative-only quote, validation failed). These are forwarded
 * by the BFF as 4xx with a structured `ExceptionDetail` body and are handled
 * by the UI — they should NOT create Sentry issues.
 */
function isUserInputServiceException(
  data: unknown,
  status: number
): data is ExceptionDetail {
  if (!isExceptionDetail(data)) return false;
  if (status < 400 || status >= 500) return false;
  // A 409 is a state CONFLICT (illegal transition / duplicate / stale cache),
  // not a user-input failure — never classify it as handled input, so it flows
  // through to capture as a warning. See IGNORED_HTTP_STATUS_CODES above.
  if (status === 409) return false;
  return !ALWAYS_BUG_EXCEPTION_TYPES.has(data.type);
}

/**
 * Why `captureRtkQueryError` will DROP this failure, or `null` if it will
 * report it. The single definition of the skip policy — `captureRtkQueryError`
 * is its only enforcement point.
 *
 * Exported because a caller that wants to raise its own event for a failure
 * the classifier drops must be able to ask, rather than re-deriving the rule
 * and drifting from it. Without this, a flow-level watchdog fires a SECOND
 * Sentry issue for every failure this function already captured — one incident,
 * two groups, double the alerts, which is exactly what `lib/logError.ts`'s
 * "don't double-send" note warns about.
 *
 * The reasons are stable strings so a caller can tag with them.
 */
export function rtkQueryCaptureSkipReason(
  error: FetchBaseQueryError,
  endpointName?: string
): string | null {
  const status = error.status;

  // Expected during normal operation and handled elsewhere.
  if (typeof status === "number" && IGNORED_HTTP_STATUS_CODES.has(status)) {
    return `ignored-status-${status}`;
  }

  // Network errors (FETCH_ERROR) — typically an in-flight request aborted
  // during navigation/unmount, or a browser-throttled background-tab poll.
  // Not actionable server errors.
  if (status === "FETCH_ERROR") {
    return "fetch-error";
  }

  // 409s on endpoints where a conflict is a routine business outcome the UI
  // already explains. Narrow by endpoint on purpose — every other 409 still
  // captures as a warning. See ROUTINE_CONFLICT_ENDPOINTS.
  if (
    status === 409 &&
    endpointName &&
    ROUTINE_CONFLICT_ENDPOINTS.has(endpointName)
  ) {
    return "routine-conflict-endpoint";
  }

  // User-input failures forwarded as a structured 4xx ExceptionDetail (invalid
  // OTP, indicative quote, validation failed). The UI surfaces these to the
  // user; they're not bugs.
  if (
    typeof status === "number" &&
    isUserInputServiceException(error.data, status)
  ) {
    return "user-input-service-exception";
  }

  return null;
}

/** sessionStorage key for the most recent upstream incident reference. */
const INCIDENT_REFERENCE_KEY = "error.lastIncident";

/** Window of time (ms) within which a stored incident reference is shown. */
const INCIDENT_REFERENCE_TTL_MS = 30_000;

/**
 * Read the most recent incident reference, if any was stored in the last
 * `INCIDENT_REFERENCE_TTL_MS`. Used by route-level error.tsx pages to
 * render a "Reference: <id>" line for users to quote to support.
 */
export function readRecentIncidentReference(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(INCIDENT_REFERENCE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      reference?: string;
      timestamp?: number;
    };
    if (
      typeof parsed.reference !== "string" ||
      typeof parsed.timestamp !== "number"
    ) {
      return null;
    }
    if (Date.now() - parsed.timestamp > INCIDENT_REFERENCE_TTL_MS) return null;
    return parsed.reference;
  } catch {
    return null;
  }
}

/**
 * Persist the upstream traceId (or spanId fallback) for `internal-service-exception`
 * errors so the route-level error.tsx can surface "Reference: <id>" for the
 * user to quote to support. Stores a timestamp so stale references aren't shown.
 */
function persistIncidentReference(detail: ExceptionDetail): void {
  if (typeof window === "undefined") return;
  const traceId =
    "traceId" in detail &&
    typeof (detail as { traceId?: unknown }).traceId === "string"
      ? (detail as { traceId: string }).traceId
      : undefined;
  const reference = traceId ?? detail.spanId;
  if (!reference) return;
  try {
    sessionStorage.setItem(
      INCIDENT_REFERENCE_KEY,
      JSON.stringify({ reference, timestamp: Date.now() })
    );
  } catch {
    // sessionStorage unavailable (private mode, quota exceeded) — non-critical.
  }
}

/**
 * Network/transport failures that mean the BFF couldn't be reached. Collapse
 * all of these into a single `infra-unavailable` fingerprint so a single
 * backend incident creates one issue, not seven.
 *
 * A `PARSING_ERROR` is judged by the REAL http status behind it, not by the
 * parse failure itself:
 *  - gateway 5xx → a proxy/WAF error page. Nothing served the request; infra.
 *  - anything else (notably a 2xx) → something answered and claimed success
 *    with a body we couldn't read. That is a malformed-response bug owned by
 *    whoever produced it, NOT an outage — see `captureMalformedResponse`.
 * Counting the latter as an outage inflates the upstream-availability report
 * sent to backend (`.claude/docs/upstream-outage-history.md`). CUSTOMER-UI-6Y.
 */
function isInfraFailure(
  status: number | string,
  transport: TransportFailure | null,
  parsing: ParsingErrorSummary | null
): boolean {
  if (parsing) {
    return GATEWAY_STATUSES.has(parsing.originalStatus);
  }
  // TIMEOUT_ERROR: the client-side abort from the bounded GET timeout
  // (withBootFetchTimeout.ts) after the retry layer is exhausted — same
  // "couldn't get an answer" incident class as a gateway 5xx, and it also
  // fires benignly when a backgrounded tab throttles a fetch past the timer.
  // One infra fingerprint, not one issue per endpoint.
  //
  // NB the direct-status list stays 502/504 rather than reusing
  // GATEWAY_STATUSES: routing a direct 503 here as well would be a behaviour
  // change beyond this ticket, and it would cost those events their `origin`
  // and `exceptionType` tags and their `persistIncidentReference` call (which
  // route-level error.tsx reads back to show the user a support reference).
  if (status === 502 || status === 504 || status === "TIMEOUT_ERROR") {
    return true;
  }
  // The BFF reported that it could not reach an upstream: the transport code
  // in the body, or the message text for bodies that carry no code. Decided
  // by `readTransportFailure`, the same rule the retry policy uses.
  return transport !== null;
}

/**
 * The infra grouping key. A body that carries the transport code groups by
 * that code alone, so one outage reported against several upstream addresses
 * is one issue. A body with only message text keeps that text as its key, as
 * before the code was read.
 */
function describeInfraCause(
  status: number | string,
  transport: TransportFailure | null,
  exceptionDetail: ExceptionDetail | null,
  parseCause: string | null
): string {
  if (parseCause) return parseCause;
  if (transport?.source === "code") return transport.code;
  return exceptionDetail?.message ?? String(status);
}

/**
 * Classify the SHAPE of an unparseable response body without echoing any of
 * its bytes (the body may contain BFF-filtered customer data). Reads only the
 * first non-whitespace character:
 *  - `html`      → starts with `<` — usually a gateway / proxy / WAF error
 *                  page (nginx 502, CloudFront/Amplify 504). NOTE this is a
 *                  filterable tag only: routing keys on `originalStatus`, not
 *                  on shape, so an HTML body served with a 200 (a captive
 *                  portal or WAF interstitial) is `[Malformed]`, not `[Infra]`.
 *  - `json-like` → starts with `{`/`[` — a truncated or malformed JSON body,
 *                  which usually IS a BFF bug worth chasing.
 *  - `empty`     → blank/whitespace-only body (often a 204 / dropped connection).
 *  - `text`      → anything else (plain-text proxy message, etc.).
 */
function classifyBodyShape(body: string): string {
  const firstNonWhitespace = body.match(/\S/);
  if (!firstNonWhitespace) return "empty";
  switch (firstNonWhitespace[0]) {
    case "<":
      return "html";
    case "{":
    case "[":
      return "json-like";
    default:
      return "text";
  }
}

/**
 * Summarize an RTK Query `PARSING_ERROR` `.error` message down to PII-free
 * STRUCTURAL facts — never the echoed body excerpt.
 *
 * The raw SyntaxError message embeds a slice of the response body
 * (V8: `Unexpected token 'N', "{\"x\":NOAH…"`; JSC: `JSON Parse error:
 * Unexpected identifier "NOAH"`). Redaction-by-subtraction (stripping quoted
 * excerpts) is NOT safe: unquoted JSON values and the single-quoted offending
 * token straddle quote boundaries, so customer-data bytes survive. So we never
 * slice the message — we map it to a fixed `kind` vocabulary and extract only
 * the numeric parse position. The body's shape is already conveyed by the
 * `responseBodyKind` / `responseBodyLength` fields set at the call site.
 */
function summarizeParseError(raw: string): string {
  const lower = raw.toLowerCase();
  const kind =
    lower.includes("end of json input") || lower.includes("unexpected eof")
      ? "truncated"
      : lower.includes("non-whitespace")
        ? "trailing-content"
        : lower.includes("unexpected token") ||
            lower.includes("unexpected identifier") ||
            lower.includes("unexpected number") ||
            lower.includes("unexpected string")
          ? "unexpected-token"
          : "parse-error";
  const position = raw.match(/position (\d+)/i);
  return position ? `${kind} @${position[1]}` : kind;
}

/**
 * PII-free, structure-only summary of an RTK Query `PARSING_ERROR`. Every
 * field is derived; none of the response body or the raw parse message
 * survives into it.
 */
interface ParsingErrorSummary {
  /** The REAL http status the parse failure happened on (200, 502, …). */
  originalStatus: number;
  parseError: string;
  bodyKind: string;
  bodyLength: number;
}

/** Non-null only for `PARSING_ERROR` — the discriminator both branches key on. */
function summarizeParsingError(
  error: FetchBaseQueryError
): ParsingErrorSummary | null {
  if (error.status !== "PARSING_ERROR") return null;
  return {
    originalStatus: error.originalStatus,
    parseError: summarizeParseError(error.error),
    bodyKind: classifyBodyShape(error.data),
    bodyLength: error.data.length,
  };
}

/**
 * Fold the real HTTP status and the body's shape into the grouping key, so a
 * gateway 5xx (HTML error page) and a malformed 200 (truncated JSON, or no
 * body at all) never share one Sentry issue — different owners, different fixes.
 */
function describeParsingCause(parsing: ParsingErrorSummary): string {
  return `PARSING_ERROR (HTTP ${parsing.originalStatus}, ${parsing.bodyKind})`;
}

/** Shared by the infra and malformed-response branches so they stay filterable alike. */
function applyParsingTags(
  scope: Sentry.Scope,
  parsing: ParsingErrorSummary
): void {
  // Real status behind the parse failure — filterable in Sentry to see the
  // gateway-5xx-vs-2xx split.
  scope.setTag("httpOriginalStatus", String(parsing.originalStatus));
  scope.setTag("responseBodyKind", parsing.bodyKind);
  scope.setExtra("parseError", parsing.parseError);
  scope.setExtra("responseBodyLength", parsing.bodyLength);
}

/**
 * Get severity level based on HTTP status code
 * - 5xx errors are critical issues → "error"
 * - 4xx errors are client issues → "warning"
 */
function getSeverityFromStatus(status: number | string): Sentry.SeverityLevel {
  const numStatus = typeof status === "number" ? status : parseInt(status, 10);
  return numStatus >= 500 ? "error" : "warning";
}

interface ErrorContext {
  url?: string;
  method?: string;
  endpointName?: string;
  requestBody?: unknown;
  /**
   * OTP request state at the time of the error, if applicable.
   * Tagged on the Sentry scope as `otpState` so OTP-gated flows
   * (trust, payment, withdrawal) can be filtered/grouped distinctly
   * from generic API errors.
   */
  otpState?: OtopRequestState;
}

/**
 * Capture RTK Query errors to Sentry with rich context.
 *
 * - 403, 404: Reported as "warning" with dedicated tags for filtering
 * - 401, 422: Ignored (token expiry / client-side validation errors)
 * - 5xx: Reported as "error" severity
 * - 409, 400, 429, other 4xx: Reported as "warning" severity
 * - Network/parsing errors: Reported as "error"
 * - TIMEOUT_ERROR: Collapsed into the infra-unavailable fingerprint (the
 *   bounded-GET abort from withBootFetchTimeout after retries exhausted)
 * - FETCH_ERROR: Ignored (aborted requests, background tab throttling)
 */
export function captureRtkQueryError(
  error: FetchBaseQueryError,
  context: ErrorContext
): void {
  const { url, method = "GET", endpointName, requestBody, otpState } = context;

  // Extract HTTP status
  const status =
    typeof error.status === "number"
      ? error.status
      : typeof error.status === "string"
        ? error.status
        : "UNKNOWN";

  // Skip expected HTTP status codes that are handled by the UI
  if (rtkQueryCaptureSkipReason(error, endpointName) !== null) {
    return;
  }

  // Detect a structured ExceptionDetail body (used both by the user-input
  // check above and the generic capture path below).
  const exceptionDetail = isExceptionDetail(error.data) ? error.data : null;

  // A `PARSING_ERROR` means the HTTP request completed but the body wasn't
  // valid JSON. RTK Query hands us the REAL http status (`originalStatus`),
  // the raw body (`data`), and the parse message (`error`) on the same
  // object — none of which the original capture recorded, leaving every
  // PARSING_ERROR indistinguishable. Surface PII-safe, structure-only
  // signals derived from all three (never the raw body or raw parse message).
  const parsing = summarizeParsingError(error);

  const parseCause = parsing ? describeParsingCause(parsing) : null;

  // Collapse infra/transport failures (ECONNREFUSED, gateway 5xx, a parse
  // failure behind one) into a single fingerprint so a single backend
  // incident == one Sentry issue.
  const transport = readTransportFailure(exceptionDetail);
  if (isInfraFailure(status, transport, parsing)) {
    const cause = describeInfraCause(
      status,
      transport,
      exceptionDetail,
      parseCause
    );

    Sentry.withScope((scope) => {
      scope.setLevel("error");
      scope.setTag("infra.unavailable", "true");
      scope.setTag("httpStatus", String(status));
      scope.setTag("httpMethod", method);
      if (endpointName) scope.setTag("endpoint", endpointName);
      if (parsing) applyParsingTags(scope, parsing);
      scope.setExtra("url", url);
      scope.setExtra("cause", cause);
      scope.setFingerprint(["infra-unavailable", cause]);
      Sentry.captureException(new Error(`[Infra] ${method} ${url}: ${cause}`));
    });
    return;
  }

  // A parse failure that is NOT behind a gateway 5xx: something answered the
  // request and claimed success, but the body couldn't be read. The canonical
  // case is CUSTOMER-UI-6Y — `GET /pricing-requests/{uuid}` returning HTTP 200
  // with a zero-length body on the payment success page. Nothing was down, so
  // this must not land in the infra bucket; it is a contract violation by
  // whichever component produced the response, and the fingerprint carries the
  // real status + body shape so each producer gets its own issue.
  if (parsing) {
    Sentry.withScope((scope) => {
      scope.setLevel("error");
      scope.setTag("response.malformed", "true");
      scope.setTag("httpStatus", String(status));
      scope.setTag("httpMethod", method);
      if (endpointName) scope.setTag("endpoint", endpointName);
      applyParsingTags(scope, parsing);
      const cause = describeParsingCause(parsing);
      scope.setExtra("url", url);
      scope.setExtra("cause", cause);
      scope.setFingerprint(["malformed-response", cause]);
      Sentry.captureException(
        new Error(`[Malformed] ${method} ${url}: ${cause}`)
      );
    });
    return;
  }

  // Report 403s as warnings — may indicate role/permission misconfiguration
  if (status === 403) {
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      scope.setTag("auth.forbidden", "true");
      scope.setTag("httpStatus", "403");
      scope.setTag("httpMethod", method);
      if (endpointName) scope.setTag("endpoint", endpointName);
      if (otpState) scope.setTag("otpState", otpState);
      scope.setExtra("url", url);
      scope.setExtra(
        "pathname",
        typeof window !== "undefined" ? window.location.pathname : "unknown"
      );
      scope.setFingerprint([
        "auth-forbidden",
        endpointName || url || "unknown",
      ]);
      Sentry.captureException(
        new Error(`[Auth] 403 Forbidden on ${method} ${url}`)
      );
    });
    return;
  }

  // Report 404s as warnings — may indicate stale IDs or race conditions
  if (status === 404) {
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      scope.setTag("resource.notFound", "true");
      scope.setTag("httpStatus", "404");
      scope.setTag("httpMethod", method);
      if (endpointName) scope.setTag("endpoint", endpointName);
      if (otpState) scope.setTag("otpState", otpState);
      scope.setExtra("url", url);
      scope.setExtra(
        "pathname",
        typeof window !== "undefined" ? window.location.pathname : "unknown"
      );
      scope.setFingerprint([
        "resource-not-found",
        endpointName || url || "unknown",
      ]);
      Sentry.captureException(
        new Error(`[Resource] 404 Not Found on ${method} ${url}`)
      );
    });
    return;
  }

  // Build error message
  const message = exceptionDetail?.message
    ? `${method} ${url}: ${exceptionDetail.message}`
    : `${method} ${url}: HTTP ${status}`;

  // Determine severity level
  const level: Sentry.SeverityLevel =
    typeof status === "number" ? getSeverityFromStatus(status) : "error";

  Sentry.withScope((scope) => {
    scope.setLevel(level);

    // Set tags for filtering in Sentry dashboard
    scope.setTag("httpStatus", String(status));
    scope.setTag("httpMethod", method);

    if (endpointName) {
      scope.setTag("endpoint", endpointName);
    }

    if (otpState) {
      scope.setTag("otpState", otpState);
    }

    if (exceptionDetail) {
      scope.setTag("origin", exceptionDetail.origin);
      scope.setTag("exceptionType", exceptionDetail.type);

      // Surface the upstream traceId/spanId in the error UX so users can
      // quote it to support, which links straight into upstream tracing.
      if (exceptionDetail.type === "internal-service-exception") {
        persistIncidentReference(exceptionDetail);
      }
    }

    // Everything below is customer-derived and must be redacted BEFORE it
    // touches the scope. `sharedBeforeSend` cannot be relied on to catch it:
    // it only knows about `contexts.response.body` and `extra["Response Body"]`,
    // neither of which is a key this function sets — so for as long as these
    // were raw, the whole create-beneficiary request body (name, IBAN, BIC,
    // address) reached Sentry on every captured failure. Redacting at the
    // source is the fix; the beforeSend strip is now belt-and-braces.
    //
    // One shared `redact` with `lib/logError.ts`, deliberately: two drifting
    // redactors is how the gap reappears.
    const safeError = redact(error);
    const safeRequestBody =
      requestBody === undefined ? undefined : redact(requestBody);
    const safeResponseBody =
      error.data === undefined ? undefined : redact(error.data);

    // Set extra context for debugging
    scope.setExtra("url", url);
    scope.setExtra("method", method);
    scope.setExtra("rawError", safeError);

    if (exceptionDetail) {
      scope.setExtra("exceptionDetail", redact(exceptionDetail));

      // SpanId is useful for cross-service correlation
      if (exceptionDetail.spanId) {
        scope.setExtra("spanId", exceptionDetail.spanId);
      }

      // `parameters` is a free-form Record<string,string> of per-instance
      // values — whatever the customer typed. The key names are schema and
      // are worth keeping; the values are not.
      if (exceptionDetail.parameters) {
        scope.setExtra(
          "parameterKeys",
          Object.keys(exceptionDetail.parameters)
        );
      }

      // Detailed messages, with the `(value=…)` tail scrubbed by `redact`.
      // The attribute name and the pattern survive — that is the part that
      // made CUSTOMER-UI-3Q diagnosable.
      if (exceptionDetail.messages) {
        scope.setExtra("messages", redact(exceptionDetail.messages));
      }
    }

    // Add request/response bodies as structured contexts for the Sentry UI
    if (safeRequestBody !== undefined) {
      scope.setContext("Request Body", {
        body:
          typeof safeRequestBody === "string"
            ? safeRequestBody
            : JSON.stringify(safeRequestBody, null, 2),
      });
    }

    if (safeResponseBody !== undefined) {
      scope.setContext("Response Body", {
        body:
          typeof safeResponseBody === "string"
            ? safeResponseBody
            : JSON.stringify(safeResponseBody, null, 2),
      });
    }

    // Add breadcrumb for timeline visibility
    Sentry.addBreadcrumb({
      category: "api.error",
      message: `${method} ${url} → ${status}`,
      level,
      data: {
        endpoint: endpointName,
        ...(safeRequestBody !== undefined
          ? {
              requestBody:
                typeof safeRequestBody === "string"
                  ? safeRequestBody
                  : JSON.stringify(safeRequestBody),
            }
          : {}),
        ...(safeResponseBody !== undefined
          ? {
              responseBody:
                typeof safeResponseBody === "string"
                  ? safeResponseBody
                  : JSON.stringify(safeResponseBody),
            }
          : {}),
      },
    });

    // Custom fingerprint for better Sentry grouping
    // Group by: endpoint + status + exception type (if available)
    const fingerprint = [
      endpointName || url || "unknown-endpoint",
      String(status),
    ];

    if (exceptionDetail?.type) {
      fingerprint.push(exceptionDetail.type);
    }

    scope.setFingerprint(fingerprint);

    // Use captureException so event.exception.values is populated,
    // which triggers the replay integration to flush its buffer
    Sentry.captureException(new Error(message));
  });
}
